import {
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { challenge as ChallengeGen } from '@valvur/shared-grpc';
import { InputFieldComponent } from '../../../../../shared/components/ui/input-field/input-field.component';
import { ParentButtonComponent } from '../../../../../shared/components/ui/parent-button/parent-button.component';
import { CourseService } from '../../../../courses/services/course.service';
import type { CourseEditDialogData } from '../../models/course-edit-dialog.data';
import type {
  UpdateValvurCoursePayload,
  ValvurCourseAssetType,
} from '../../models/update-valvur-course.model';

interface LessonFormRow {
  taskId: string;
  title: string;
  description: string;
  contentUrl: string;
  audioUrl: string;
  scriptUrl: string;
  orderIndex: number;
  estimatedTime: number;
}

@Component({
  selector: 'app-course-edit-modal',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    TranslatePipe,
    InputFieldComponent,
    ParentButtonComponent,
  ],
  templateUrl: './course-edit-modal.component.html',
  styleUrl: './course-edit-modal.component.scss',
})
export class CourseEditModalComponent {
  readonly #courseService = inject(CourseService);
  readonly #dialogRef = inject(
    MatDialogRef<CourseEditModalComponent, ChallengeGen.Course | undefined>,
  );
  readonly #data = inject<CourseEditDialogData>(MAT_DIALOG_DATA);

  readonly course = signal<ChallengeGen.Course>(this.#data.course);

  readonly isSaving = computed(() =>
    this.#courseService.updateValvurCourse.isPending(),
  );
  readonly isUploading = computed(() =>
    this.#courseService.uploadValvurCourseAsset.isPending(),
  );

  readonly courseName = signal('');
  readonly courseDescription = signal('');
  readonly category = signal('');
  readonly estimatedDuration = signal(0);
  readonly courseImageUrl = signal('');
  readonly identityMandatory = signal(false);
  readonly lessons = signal<LessonFormRow[]>([]);
  readonly saveError = signal<string | null>(null);

  readonly courseCoverPreviewUrl = computed(
    () => this.course().courseImageUrl?.trim() || '',
  );

  constructor() {
    effect(() => {
      const c = this.course();
      this.courseName.set(c.courseName ?? '');
      this.courseDescription.set(c.courseDescription ?? '');
      this.category.set(c.category ?? '');
      this.estimatedDuration.set(c.estimatedDuration ?? 0);
      this.courseImageUrl.set('');
      this.identityMandatory.set(c.identityMandatory === true);
      const tasks = [...(c.tasks ?? [])].sort(
        (a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0),
      );
      this.lessons.set(
        tasks.map((t) => ({
          taskId: t.id,
          title: t.title || t.name || '',
          description: t.description ?? '',
          contentUrl: t.contentUrl ?? '',
          audioUrl: t.audioUrl ?? '',
          scriptUrl: t.scriptUrl ?? '',
          orderIndex: t.orderIndex ?? 0,
          estimatedTime: t.estimatedTime ?? 0,
        })),
      );
      this.saveError.set(null);
    });
  }

  onDiscard(): void {
    this.#dialogRef.close();
  }

  async onSave(): Promise<void> {
    const c = this.course();
    if (!c?.id) return;

    const payload: UpdateValvurCoursePayload = {
      courseName: this.courseName().trim(),
      courseDescription: this.courseDescription().trim(),
      category: this.category().trim(),
      estimatedDuration: this.estimatedDuration(),
      identityMandatory: this.identityMandatory(),
      tasks: this.lessons().map((lesson) => ({
        taskId: lesson.taskId,
        title: lesson.title.trim(),
        description: lesson.description.trim(),
        orderIndex: lesson.orderIndex,
        estimatedTime: lesson.estimatedTime,
        ...(lesson.contentUrl.trim()
          ? { contentUrl: lesson.contentUrl.trim() }
          : {}),
        ...(lesson.audioUrl.trim() ? { audioUrl: lesson.audioUrl.trim() } : {}),
        ...(lesson.scriptUrl.trim()
          ? { scriptUrl: lesson.scriptUrl.trim() }
          : {}),
      })),
    };

    if (this.courseImageUrl().trim()) {
      payload.courseImageUrl = this.courseImageUrl().trim();
    }

    this.saveError.set(null);
    try {
      const updated = await this.#courseService.updateValvurCourse.mutateAsync({
        courseId: c.id,
        ...payload,
      });
      this.#dialogRef.close(updated);
    } catch {
      this.saveError.set('app.admin.contentPage.edit.saveError');
    }
  }

  async onCoverSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    await this.uploadAsset('COVER', file);
  }

  async onLessonThumbnailSelected(
    taskId: string,
    event: Event,
  ): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    await this.uploadAsset('TASK_THUMBNAIL', file, taskId);
  }

  private async uploadAsset(
    assetType: ValvurCourseAssetType,
    file: File,
    taskId?: string,
  ): Promise<void> {
    const courseId = this.course().id;
    if (!courseId) return;
    this.saveError.set(null);
    try {
      const updated =
        await this.#courseService.uploadValvurCourseAsset.mutateAsync({
          courseId,
          assetType,
          taskId,
          file,
        });
      this.course.set(updated);
    } catch {
      this.saveError.set('app.admin.contentPage.edit.uploadError');
    }
  }

  updateLesson(
    taskId: string,
    field: keyof LessonFormRow,
    value: string | number,
  ): void {
    this.lessons.update((rows) =>
      rows.map((row) =>
        row.taskId === taskId ? { ...row, [field]: value } : row,
      ),
    );
  }

  lessonCoverUrl(taskId: string): string {
    const task = this.course().tasks?.find((t) => t.id === taskId);
    if (!task) return '';
    return (task.coverUrl || task.thumbnailUrl || '').trim();
  }
}
